import React, { useState, useEffect, useCallback } from 'react';
import { 
  GraduationCap, BookOpen, Clock, CheckCircle, XCircle, 
  Users, Play, Award, ChevronRight, ChevronLeft, AlertCircle,
  Shield, Target, TrendingUp, Calendar, Bell, Plus, Trash2,
  FileText, ArrowRight, RefreshCw, Eye, BarChart3, Filter,
  Search, Download, Timer, Zap, AlertTriangle, CheckSquare,
  Star, Bookmark, PauseCircle, PlayCircle, Volume2, VolumeX,
  Maximize2, Minimize2, List, Grid3X3, Trophy, Medal, Info,
  Flame, Lock, Globe, Lightbulb, Layers, ChevronDown, X,
  CircleDot, Sparkles, HelpCircle, ArrowLeft, RotateCcw
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_INFO = {
  compliance: { label: 'Compliance', color: '#EF4444', accent: 'from-red-500 to-rose-600', icon: Shield, bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-400' },
  safety: { label: 'Safety', color: '#F97316', accent: 'from-orange-500 to-amber-600', icon: AlertTriangle, bg: 'bg-orange-500/10', border: 'border-orange-500/20', text: 'text-orange-400' },
  skills: { label: 'Skills', color: '#3B82F6', accent: 'from-blue-500 to-indigo-600', icon: Target, bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400' },
  onboarding: { label: 'Onboarding', color: '#10B981', accent: 'from-emerald-500 to-teal-600', icon: Users, bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400' },
  product: { label: 'Product', color: '#8B5CF6', accent: 'from-violet-500 to-purple-600', icon: Zap, bg: 'bg-violet-500/10', border: 'border-violet-500/20', text: 'text-violet-400' }
};

const DIFFICULTY_INFO = {
  beginner: { label: 'Beginner', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  intermediate: { label: 'Intermediate', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  advanced: { label: 'Advanced', color: 'bg-red-500/15 text-red-400 border-red-500/30' }
};

const EPortalPageRevamped = ({ getAuthHeaders, currentUser }) => {
  const [modules, setModules] = useState([]);
  const [myLearning, setMyLearning] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [teamProgress, setTeamProgress] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [viewMode, setViewMode] = useState('my-learning');
  const [selectedModule, setSelectedModule] = useState(null);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showSlideshowModal, setShowSlideshowModal] = useState(false);
  const [showExamModal, setShowExamModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideshowCompleted, setSlideshowCompleted] = useState(false);
  const [examAnswers, setExamAnswers] = useState([]);
  const [examResult, setExamResult] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [assignForm, setAssignForm] = useState({
    staff_username: '', module_id: '', due_date: '', notes: '', priority: 'normal'
  });

  const isManager = currentUser?.role === 'manager';
  const isExecutive = ['superadmin', 'executive', 'admin'].includes(currentUser?.role);
  const canManageTraining = isManager || isExecutive;

  const fetchData = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    try {
      const [modulesRes, learningRes, remindersRes, certsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/eportal/modules`, { headers }),
        axios.get(`${BACKEND_URL}/api/eportal/my-learning`, { headers }),
        axios.get(`${BACKEND_URL}/api/eportal/reminders`, { headers }),
        axios.get(`${BACKEND_URL}/api/eportal/certificates`, { headers })
      ]);
      setModules(modulesRes.data);
      setMyLearning(learningRes.data);
      setReminders(remindersRes.data);
      setCertificates(certsRes.data);
      if (canManageTraining) {
        const [teamRes, staffRes, analyticsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/eportal/team-progress`, { headers }),
          axios.get(`${BACKEND_URL}/api/eportal/staff-list`, { headers }),
          axios.get(`${BACKEND_URL}/api/eportal/analytics/overview`, { headers })
        ]);
        setTeamProgress(teamRes.data);
        setStaffList(staffRes.data);
        setAnalytics(analyticsRes.data);
      }
    } catch (error) {
      console.error('Error fetching E-Portal data:', error);
    }
  }, [getAuthHeaders, canManageTraining]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openModule = async (moduleId, assignment = null) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${BACKEND_URL}/api/eportal/modules/${moduleId}`, { headers });
      setSelectedModule(response.data);
      setCurrentAssignment(assignment);
      setShowModuleModal(true);
    } catch (error) {
      toast.error('Failed to load module');
    }
  };

  const startSlideshow = () => {
    if (!selectedModule?.slides?.length) { toast.error('No slides available'); return; }
    setCurrentSlide(0);
    setSlideshowCompleted(false);
    setShowModuleModal(false);
    setShowSlideshowModal(true);
  };

  const nextSlide = async () => {
    const totalSlides = selectedModule?.slides?.length || 0;
    if (currentSlide < totalSlides - 1) {
      const newSlide = currentSlide + 1;
      setCurrentSlide(newSlide);
      if (currentAssignment) {
        try {
          const headers = getAuthHeaders();
          await axios.post(`${BACKEND_URL}/api/eportal/progress/${currentAssignment.id}/slide/${newSlide + 1}`, {}, { headers });
        } catch (error) { console.error('Failed to update progress:', error); }
      }
    } else {
      setSlideshowCompleted(true);
    }
  };

  const prevSlide = () => { if (currentSlide > 0) setCurrentSlide(currentSlide - 1); };

  const startExam = () => {
    if (!selectedModule?.questions?.length) { toast.error('No questions available'); return; }
    setExamAnswers(new Array(selectedModule.questions.length).fill(-1));
    setExamResult(null);
    setShowSlideshowModal(false);
    setShowExamModal(true);
  };

  const submitExam = async () => {
    if (!currentAssignment) { toast.error('No assignment found'); return; }
    if (examAnswers.includes(-1)) { toast.error('Please answer all questions'); return; }
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${BACKEND_URL}/api/eportal/exam/${currentAssignment.id}/submit`, { answers: examAnswers }, { headers });
      setExamResult(response.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit exam');
    }
  };

  const handleAssign = async () => {
    if (!assignForm.staff_username || !assignForm.module_id) { toast.error('Please select staff and module'); return; }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/eportal/assign`, assignForm, { headers });
      toast.success('Training assigned successfully');
      setShowAssignModal(false);
      setAssignForm({ staff_username: '', module_id: '', due_date: '', notes: '', priority: 'normal' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign training');
    }
  };

  const deleteAssignment = async (assignmentId) => {
    if (!window.confirm('Remove this assignment?')) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/eportal/assignments/${assignmentId}`, { headers });
      toast.success('Assignment removed');
      fetchData();
    } catch (error) { toast.error('Failed to remove assignment'); }
  };

  const getCategoryInfo = (category) => CATEGORY_INFO[category] || CATEGORY_INFO.skills;
  const getDifficultyInfo = (difficulty) => DIFFICULTY_INFO[difficulty] || DIFFICULTY_INFO.beginner;

  const filteredModules = modules.filter(m => {
    const matchesCategory = categoryFilter === 'all' || m.category === categoryFilter;
    const matchesSearch = m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         m.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const pendingCount = myLearning.filter(a => !a.passed).length;
  const completedCount = myLearning.filter(a => a.passed).length;
  const complianceAlerts = reminders.filter(r => r.type === 'compliance_expiring' || r.type === 'compliance_expired').length;

  const renderContent = (content) => {
    if (!content) return null;
    return content.split('\n').map((line, idx) => {
      if (line.startsWith('## ')) return <h2 key={idx} className="text-xl font-bold text-white mt-5 mb-3">{line.replace('## ', '')}</h2>;
      if (line.startsWith('### ')) return <h3 key={idx} className="text-base font-semibold text-[#00F0FF] mt-4 mb-2">{line.replace('### ', '')}</h3>;
      if (line.startsWith('- **')) {
        const match = line.match(/- \*\*(.+?)\*\*(.*)$/);
        if (match) return <div key={idx} className="flex items-start gap-2.5 ml-4 my-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] mt-2 flex-shrink-0" /><span><strong className="text-white">{match[1]}</strong><span className="text-white/50">{match[2]}</span></span></div>;
      }
      if (line.startsWith('- ')) return <div key={idx} className="flex items-start gap-2.5 ml-4 my-1"><span className="w-1.5 h-1.5 rounded-full bg-zinc-500 mt-2 flex-shrink-0" /><span className="text-zinc-300">{line.replace('- ', '')}</span></div>;
      if (/^\d+\.\s/.test(line)) {
        const num = line.match(/^(\d+)/)[1];
        return <div key={idx} className="flex items-start gap-3 ml-4 my-1.5"><span className="w-6 h-6 rounded-md bg-[#00F0FF]/10 text-[#00F0FF] flex items-center justify-center text-xs font-bold flex-shrink-0">{num}</span><span className="text-zinc-300">{line.replace(/^\d+\.\s/, '')}</span></div>;
      }
      if (line.startsWith('**') && line.endsWith('**')) return <p key={idx} className="text-white font-semibold mt-3">{line.replace(/\*\*/g, '')}</p>;
      if (line.trim() === '') return <div key={idx} className="h-3" />;
      return <p key={idx} className="text-zinc-300 leading-relaxed">{line}</p>;
    });
  };

  const getStatusInfo = (status, passed) => {
    if (passed) return { color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25', label: 'Completed', icon: CheckCircle };
    switch (status) {
      case 'in_progress': return { color: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/25', label: 'In Progress', icon: PlayCircle };
      case 'slides_completed': return { color: 'bg-amber-500/15 text-amber-400 border-amber-500/25', label: 'Ready for Exam', icon: FileText };
      default: return { color: 'bg-zinc-500/15 text-white/50 border-zinc-500/25', label: 'Not Started', icon: CircleDot };
    }
  };

  const VIEW_TABS = [
    { id: 'my-learning', label: 'My Learning', icon: BookOpen, count: pendingCount },
    { id: 'catalog', label: 'Course Catalog', icon: Layers, count: modules.length },
    { id: 'certificates', label: 'Certificates', icon: Award, count: certificates.length },
    ...(canManageTraining ? [
      { id: 'team', label: 'Team Progress', icon: Users },
      { id: 'analytics', label: 'Analytics', icon: BarChart3 }
    ] : [])
  ];

  return (
    <div className="space-y-8" data-testid="eportal-page">
      {/* ═══ PREMIUM HERO ═══ */}
      <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-[#0a0d1a] via-[#0b0a15] to-[#0a0d1a] p-6 lg:p-8">
        <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full blur-3xl opacity-25" style={{ background: 'radial-gradient(circle, #00E5FF 0%, transparent 70%)' }} />
        <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-20" style={{ background: 'radial-gradient(circle, #8A2BE2 0%, transparent 70%)' }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="relative">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-[0.2em]">
                <GraduationCap className="w-3.5 h-3.5" style={{ color: '#00E5FF' }} />
                Learning &amp; development
              </div>
              <h1 className="mt-2 text-3xl lg:text-4xl font-light text-white" data-testid="eportal-title">
                E-<span className="italic font-serif" style={{ color: '#00E5FF' }}>Portal</span>
              </h1>
              <p className="mt-2 text-white/40 text-sm">Your training path, certifications and compliance in one place.</p>
              {complianceAlerts > 0 && (
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F43F5E]/10 border border-[#F43F5E]/30 text-[#fca5a5]" data-testid="compliance-alert">
                  <AlertTriangle className="w-3.5 h-3.5 animate-pulse" />
                  <span className="text-xs font-medium">{complianceAlerts} compliance alert{complianceAlerts > 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {canManageTraining && (
                <Button
                  onClick={() => setShowAssignModal(true)}
                  className="bg-gradient-to-r from-[#00E5FF]/25 to-[#8A2BE2]/25 border border-[#00E5FF]/30 text-white hover:from-[#00E5FF]/35 hover:to-[#8A2BE2]/35 rounded-full px-5"
                  data-testid="assign-training-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Assign training
                </Button>
              )}
            </div>
          </div>

          {/* Stats Rail */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-8">
            {[
              { label: 'Assigned',    value: myLearning.length, icon: BookOpen, hex: '#00E5FF' },
              { label: 'Completed',   value: completedCount,    icon: CheckCircle, hex: '#10B981' },
              { label: 'Pending',     value: pendingCount,      icon: Clock, hex: '#F59E0B' },
              { label: 'Certificates',value: certificates.length, icon: Trophy, hex: '#8A2BE2' },
              { label: 'Compliant',   value: myLearning.filter(a => a.compliance_required && a.compliance_status === 'compliant').length, icon: Shield, hex: '#F43F5E' }
            ].map((stat, i) => (
              <div key={i} className="group relative overflow-hidden bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-4 hover:border-white/15 transition-all" data-testid={`stat-${stat.label.toLowerCase()}`}>
                <span className="absolute inset-y-0 left-0 w-[3px]" style={{ background: `linear-gradient(to bottom, ${stat.hex}, ${stat.hex}00)` }} />
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${stat.hex}18`, boxShadow: `0 0 20px ${stat.hex}18` }}>
                    <stat.icon className="w-5 h-5" style={{ color: stat.hex }} />
                  </div>
                  <div>
                    <p className="text-2xl font-light text-white">{stat.value}</p>
                    <p className="text-[10px] text-white/40 uppercase tracking-wider font-medium">{stat.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reminders */}
      {reminders.length > 0 && (
        <div className="bg-red-500/5 border border-red-500/15 rounded-xl p-5" data-testid="reminders-section">
          <div className="flex items-start gap-3">
            <Bell className="w-5 h-5 text-red-400 mt-0.5 animate-pulse flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider">Action Required</h3>
              <div className="space-y-2">
                {reminders.slice(0, 3).map((r, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-black/30 rounded-lg border border-white/5">
                    <div>
                      <span className="text-white text-sm font-medium">{r.module_title}</span>
                      <span className="text-white/40 text-xs ml-2">
                        {r.type === 'due_soon' && `Due in ${r.days_remaining} days`}
                        {r.type === 'compliance_expiring' && `Expires in ${r.days_remaining} days`}
                        {r.type === 'compliance_expired' && 'Certification expired'}
                      </span>
                    </div>
                    <Button size="sm" onClick={() => openModule(r.module_id, myLearning.find(a => a.id === r.assignment_id))}
                      className="bg-red-500 hover:bg-red-600 text-white text-xs px-3" data-testid={`reminder-start-${idx}`}>
                      Start Now
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-white/5">
        <div className="flex gap-1 overflow-x-auto pb-px">
          {VIEW_TABS.map(tab => (
            <button key={tab.id} onClick={() => setViewMode(tab.id)} data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium whitespace-nowrap transition-all border-b-2 ${
                viewMode === tab.id
                  ? 'text-[#00F0FF] border-[#00F0FF]'
                  : 'text-white/40 border-transparent hover:text-zinc-300 hover:border-zinc-700'
              }`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                  viewMode === tab.id ? 'bg-[#00F0FF]/20 text-[#00F0FF]' : 'bg-white/5 text-white/40'
                }`}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* My Learning View */}
      {viewMode === 'my-learning' && (
        <div className="space-y-4" data-testid="my-learning-view">
          {myLearning.length === 0 ? (
            <div className="text-center py-16 bg-[#0D0D14] border border-white/5 rounded-xl">
              <GraduationCap className="w-16 h-16 mx-auto mb-4 text-zinc-700" />
              <h3 className="text-xl font-semibold text-white mb-2">No Training Assigned</h3>
              <p className="text-white/40 mb-5">Browse the course catalog to see available modules</p>
              <Button onClick={() => setViewMode('catalog')} className="bg-gradient-to-r from-[#00F0FF] to-[#B026FF] text-white" data-testid="browse-catalog-btn">
                Browse Catalog
              </Button>
            </div>
          ) : (
            myLearning
              .sort((a, b) => {
                if (a.compliance_status === 'expired' && b.compliance_status !== 'expired') return -1;
                if (b.compliance_status === 'expired' && a.compliance_status !== 'expired') return 1;
                if (!a.passed && b.passed) return -1;
                if (a.passed && !b.passed) return 1;
                return 0;
              })
              .map(assignment => {
                const catInfo = getCategoryInfo(assignment.module_category);
                const statusInfo = getStatusInfo(assignment.status, assignment.passed);
                const CatIcon = catInfo.icon;
                const StatusIcon = statusInfo.icon;
                return (
                  <div key={assignment.id} data-testid={`assignment-${assignment.id}`}
                    className={`bg-[#0D0D14] border rounded-xl overflow-hidden transition-all hover:border-white/15 ${
                      assignment.compliance_status === 'expired' ? 'border-red-500/30' :
                      assignment.compliance_status === 'expiring_soon' ? 'border-amber-500/30' :
                      assignment.passed ? 'border-emerald-500/20' : 'border-white/5'
                    }`}>
                    {/* Color top bar */}
                    <div className={`h-1 bg-gradient-to-r ${catInfo.accent}`} />
                    <div className="p-5">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex items-start gap-4 min-w-0">
                          <div className={`w-12 h-12 ${catInfo.bg} ${catInfo.border} border rounded-xl flex items-center justify-center flex-shrink-0`}>
                            <CatIcon className={`w-6 h-6 ${catInfo.text}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-white font-semibold text-lg truncate">{assignment.module_title}</h3>
                            <p className="text-white/40 text-sm mt-0.5 line-clamp-1">{assignment.module_description}</p>
                            <div className="flex items-center gap-2 flex-wrap mt-3">
                              <Badge className={`${statusInfo.color} text-[11px]`}><StatusIcon className="w-3 h-3 mr-1" />{statusInfo.label}</Badge>
                              <Badge className={`${catInfo.bg} ${catInfo.text} ${catInfo.border} text-[11px]`}>{catInfo.label}</Badge>
                              {assignment.compliance_required && (
                                <Badge className="bg-red-500/15 text-red-400 border-red-500/25 text-[11px]">
                                  <Shield className="w-3 h-3 mr-1" />Compliance
                                </Badge>
                              )}
                              {assignment.is_mandatory && (
                                <Badge className="bg-violet-500/15 text-violet-400 border-violet-500/25 text-[11px]">Mandatory</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-3 text-xs text-white/40">
                              <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" />{assignment.slides_count} slides</span>
                              <span className="flex items-center gap-1"><HelpCircle className="w-3.5 h-3.5" />{assignment.questions_count} questions</span>
                              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{assignment.estimated_duration} min</span>
                              {assignment.attempts_count > 0 && (
                                <span className="flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" />{assignment.attempts_count} attempt{assignment.attempts_count > 1 ? 's' : ''}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-3 lg:min-w-[180px]">
                          {assignment.progress > 0 && assignment.progress < 100 && (
                            <div className="w-full lg:w-40">
                              <div className="flex justify-between text-[10px] text-white/40 mb-1">
                                <span>Progress</span><span>{assignment.progress}%</span>
                              </div>
                              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-[#00F0FF] to-[#B026FF] rounded-full transition-all" style={{ width: `${assignment.progress}%` }} />
                              </div>
                            </div>
                          )}
                          {assignment.best_score !== null && (
                            <p className="text-xs text-white/40">Best: <span className={assignment.passed ? 'text-emerald-400' : 'text-amber-400'}>{assignment.best_score}%</span></p>
                          )}
                          <Button onClick={() => openModule(assignment.module_id, assignment)} data-testid={`open-module-${assignment.id}`}
                            className={assignment.passed
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20'
                              : 'bg-gradient-to-r from-[#00F0FF] to-[#B026FF] text-white hover:opacity-90'
                            }>
                            {assignment.passed ? <><Eye className="w-4 h-4 mr-2" />Review</> :
                             assignment.status === 'slides_completed' ? <><Play className="w-4 h-4 mr-2" />Take Exam</> :
                             assignment.progress > 0 ? <><PlayCircle className="w-4 h-4 mr-2" />Continue</> :
                             <><Play className="w-4 h-4 mr-2" />Start</>}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
          )}
        </div>
      )}

      {/* Course Catalog View */}
      {viewMode === 'catalog' && (
        <div className="space-y-6" data-testid="catalog-view">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input type="text" placeholder="Search courses..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="catalog-search"
                className="w-full pl-10 pr-4 py-2.5 bg-[#0D0D14] border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] focus:outline-none transition-all text-sm" />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {[{ key: 'all', label: 'All' }, ...Object.entries(CATEGORY_INFO).map(([k, v]) => ({ key: k, label: v.label }))].map(f => (
                <button key={f.key} onClick={() => setCategoryFilter(f.key)} data-testid={`filter-${f.key}`}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    categoryFilter === f.key ? 'bg-[#00F0FF]/15 text-[#00F0FF] border border-[#00F0FF]/30' : 'bg-white/5 text-white/40 border border-white/5 hover:text-zinc-300'
                  }`}>{f.label}</button>
              ))}
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredModules.map(module => {
              const catInfo = getCategoryInfo(module.category);
              const diffInfo = getDifficultyInfo(module.difficulty);
              const CatIcon = catInfo.icon;
              return (
                <div key={module.id} onClick={() => openModule(module.id)} data-testid={`catalog-module-${module.id}`}
                  className="bg-[#0D0D14] border border-white/5 rounded-xl overflow-hidden cursor-pointer group hover:border-[#00F0FF]/20 transition-all hover:-translate-y-0.5">
                  <div className={`h-1 bg-gradient-to-r ${catInfo.accent}`} />
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-11 h-11 ${catInfo.bg} ${catInfo.border} border rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <CatIcon className={`w-5 h-5 ${catInfo.text}`} />
                      </div>
                      <div className="flex gap-1.5">
                        {module.compliance_required && (
                          <Badge className="bg-red-500/15 text-red-400 border-red-500/25 text-[10px]"><Shield className="w-3 h-3 mr-0.5" />Compliance</Badge>
                        )}
                      </div>
                    </div>
                    <h3 className="text-white font-semibold text-base mb-1.5 line-clamp-2 group-hover:text-[#00F0FF] transition-colors">{module.title}</h3>
                    <p className="text-white/40 text-xs mb-4 line-clamp-2 leading-relaxed">{module.description}</p>
                    <div className="flex items-center gap-1.5 mb-4">
                      <Badge className={`${catInfo.bg} ${catInfo.text} ${catInfo.border} text-[10px]`}>{catInfo.label}</Badge>
                      <Badge className={`${diffInfo.color} text-[10px]`}>{diffInfo.label}</Badge>
                    </div>
                    <div className="flex items-center justify-between text-xs text-white/30 pt-3 border-t border-white/5">
                      <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" />{module.slides?.length || 0} slides</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{module.estimated_duration} min</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {filteredModules.length === 0 && (
            <div className="text-center py-12 text-white/40">
              <Search className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-lg">No courses found</p>
              <p className="text-sm mt-1">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      )}

      {/* Certificates View */}
      {viewMode === 'certificates' && (
        <div data-testid="certificates-view">
          {certificates.length === 0 ? (
            <div className="text-center py-16 bg-[#0D0D14] border border-white/5 rounded-xl">
              <Award className="w-16 h-16 mx-auto mb-4 text-zinc-700" />
              <h3 className="text-xl font-semibold text-white mb-2">No Certificates Yet</h3>
              <p className="text-white/40">Complete training modules to earn certificates</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {certificates.map((cert, idx) => {
                const catInfo = getCategoryInfo(cert.category);
                return (
                  <div key={idx} data-testid={`certificate-${idx}`}
                    className="bg-[#0D0D14] border border-white/5 rounded-xl overflow-hidden hover:border-[#B026FF]/20 transition-all">
                    <div className="h-1 bg-gradient-to-r from-amber-400 to-orange-500" />
                    <div className="p-6 text-center">
                      <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/20 rotate-3">
                        <Trophy className="w-10 h-10 text-white" />
                      </div>
                      <h3 className="text-white font-semibold mb-1">{cert.module_title}</h3>
                      <Badge className={`${catInfo.bg} ${catInfo.text} ${catInfo.border} text-[10px] mb-3`}>{catInfo.label}</Badge>
                      <p className="text-white/40 text-xs mb-1">Completed: {new Date(cert.completed_at).toLocaleDateString()}</p>
                      <p className="text-emerald-400 font-bold text-lg">Score: {cert.score}%</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Team Progress View */}
      {viewMode === 'team' && canManageTraining && (
        <div className="bg-[#0D0D14] border border-white/5 rounded-xl" data-testid="team-progress-view">
          <div className="p-5 border-b border-white/5">
            <h2 className="text-white font-semibold flex items-center gap-2"><Users className="w-5 h-5 text-[#00F0FF]" />Team Training Progress</h2>
          </div>
          <div className="p-5">
            {teamProgress.length === 0 ? (
              <div className="text-center py-12 text-white/40"><Users className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No training assignments yet</p></div>
            ) : (
              <div className="space-y-3">
                {teamProgress.map(a => {
                  const statusInfo = getStatusInfo(a.status, a.passed);
                  return (
                    <div key={a.id} className="p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-white/10 transition-all">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00F0FF] to-[#B026FF] flex items-center justify-center text-white font-bold text-sm">
                            {a.staff_display_name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div>
                            <p className="text-white font-medium text-sm">{a.staff_display_name}</p>
                            <p className="text-white/40 text-xs">{a.module_title}</p>
                            <div className="flex items-center gap-2 mt-1.5">
                              <Badge className={`${statusInfo.color} text-[10px]`}>{statusInfo.label}</Badge>
                              {a.best_score !== null && <span className="text-white/40 text-[10px]">Score: {a.best_score}%</span>}
                            </div>
                          </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => deleteAssignment(a.id)} data-testid={`delete-assignment-${a.id}`}
                          className="border-red-500/20 text-red-400 hover:bg-red-500/10 h-8 w-8 p-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      {a.progress > 0 && a.progress < 100 && (
                        <div className="mt-3">
                          <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-[#00F0FF] to-[#B026FF] rounded-full" style={{ width: `${a.progress}%` }} />
                          </div>
                          <p className="text-[10px] text-white/30 mt-1">{a.progress}% complete</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Analytics View */}
      {viewMode === 'analytics' && canManageTraining && analytics && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="analytics-view">
          {[
            { label: 'Total Modules', value: analytics.total_modules, icon: BookOpen, color: '#00F0FF' },
            { label: 'Completion Rate', value: `${analytics.completion_rate}%`, icon: CheckCircle, color: '#10B981', showProgress: true, progress: analytics.completion_rate },
            { label: 'Average Score', value: `${analytics.average_score}%`, icon: Target, color: '#8B5CF6' },
            { label: 'In Progress', value: analytics.in_progress, icon: Clock, color: '#F59E0B' },
            { label: 'Total Assignments', value: analytics.total_assignments, icon: Users, color: '#3B82F6' },
            { label: 'Completed', value: analytics.completed_assignments, icon: Trophy, color: '#10B981' }
          ].map((s, i) => (
            <div key={i} className="bg-[#0D0D14] border border-white/5 rounded-xl p-6 hover:border-white/10 transition-all" data-testid={`analytics-${s.label.toLowerCase().replace(/\s/g,'-')}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${s.color}15` }}>
                  <s.icon className="w-5 h-5" style={{ color: s.color }} />
                </div>
                <p className="text-[11px] text-white/40 uppercase tracking-wider font-medium">{s.label}</p>
              </div>
              <p className="text-3xl font-bold text-white">{s.value}</p>
              {s.showProgress && (
                <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#00F0FF] to-[#B026FF] rounded-full" style={{ width: `${s.progress}%` }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Module Preview Modal */}
      <Dialog open={showModuleModal} onOpenChange={setShowModuleModal}>
        <DialogContent className="bg-[#0D0D14] border border-white/10 text-white max-w-2xl shadow-2xl shadow-black/50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg"><BookOpen className="w-5 h-5 text-[#00F0FF]" />{selectedModule?.title}</DialogTitle>
            <DialogDescription className="text-white/50 text-sm">{selectedModule?.description}</DialogDescription>
          </DialogHeader>
          {selectedModule && (
            <div className="space-y-5 mt-4">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: FileText, color: '#00F0FF', value: selectedModule.slides?.length || 0, label: 'Slides' },
                  { icon: HelpCircle, color: '#B026FF', value: selectedModule.questions?.length || 0, label: 'Questions' },
                  { icon: Clock, color: '#F59E0B', value: selectedModule.estimated_duration, label: 'Minutes' }
                ].map((s, i) => (
                  <div key={i} className="p-3 bg-white/[0.03] rounded-lg text-center border border-white/5">
                    <s.icon className="w-4 h-4 mx-auto mb-1" style={{ color: s.color }} />
                    <p className="text-white font-bold">{s.value}</p>
                    <p className="text-[10px] text-white/40">{s.label}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Badge className={`${getCategoryInfo(selectedModule.category).bg} ${getCategoryInfo(selectedModule.category).text} ${getCategoryInfo(selectedModule.category).border} text-[10px]`}>{getCategoryInfo(selectedModule.category).label}</Badge>
                <Badge className={`${getDifficultyInfo(selectedModule.difficulty).color} text-[10px]`}>{getDifficultyInfo(selectedModule.difficulty).label}</Badge>
                {selectedModule.compliance_required && <Badge className="bg-red-500/15 text-red-400 border-red-500/25 text-[10px]"><Shield className="w-3 h-3 mr-0.5" />Compliance</Badge>}
                {selectedModule.is_mandatory && <Badge className="bg-violet-500/15 text-violet-400 border-violet-500/25 text-[10px]">Mandatory</Badge>}
              </div>
              <div className="p-4 bg-white/[0.02] rounded-lg border border-white/5">
                <h4 className="text-white font-medium text-sm mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-[#00F0FF]" />Requirements</h4>
                <ul className="text-white/50 text-xs space-y-1">
                  <li>Complete all {selectedModule.slides?.length || 0} slides</li>
                  <li>Pass the exam with {selectedModule.passing_score}% or higher</li>
                  {selectedModule.compliance_required && <li>Recertification required every {selectedModule.compliance_period_months} months</li>}
                </ul>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowModuleModal(false)} className="border-white/10 text-white/50 hover:text-white" data-testid="close-module-modal">Close</Button>
                {currentAssignment && (
                  <Button onClick={startSlideshow} className="bg-gradient-to-r from-[#00F0FF] to-[#B026FF] text-white hover:opacity-90" data-testid="start-training-btn">
                    <Play className="w-4 h-4 mr-2" />{currentAssignment.progress > 0 ? 'Continue Training' : 'Start Training'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Slideshow Modal */}
      <Dialog open={showSlideshowModal} onOpenChange={setShowSlideshowModal}>
        <DialogContent className="bg-[#0D0D14] border border-white/10 text-white max-w-4xl max-h-[90vh] p-0 overflow-hidden shadow-2xl">
          {selectedModule && selectedModule.slides && (
            <div className="flex flex-col h-full">
              <div className="p-4 border-b border-white/5 flex items-center justify-between bg-black/30 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                  <GraduationCap className="w-5 h-5 text-[#00F0FF]" />
                  <span className="text-white font-medium text-sm">{selectedModule.title}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-white/40 text-xs">Slide {currentSlide + 1} / {selectedModule.slides.length}</span>
                  <div className="w-24 h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#00F0FF] to-[#B026FF] rounded-full transition-all" style={{ width: `${((currentSlide + 1) / selectedModule.slides.length) * 100}%` }} />
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-8">
                {!slideshowCompleted ? (
                  <div className="max-w-3xl mx-auto">
                    <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                      <span className="w-10 h-10 bg-gradient-to-br from-[#00F0FF] to-[#B026FF] rounded-xl flex items-center justify-center text-lg font-bold">{currentSlide + 1}</span>
                      {selectedModule.slides[currentSlide]?.title}
                    </h2>
                    <div className="text-sm leading-relaxed">{renderContent(selectedModule.slides[currentSlide]?.content)}</div>
                    {selectedModule.slides[currentSlide]?.notes && (
                      <div className="mt-6 p-4 bg-[#00F0FF]/5 border border-[#00F0FF]/20 rounded-xl">
                        <p className="text-[#00F0FF] text-sm flex items-start gap-2"><Info className="w-4 h-4 mt-0.5 flex-shrink-0" />{selectedModule.slides[currentSlide].notes}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="w-20 h-20 text-emerald-400 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-white mb-2">Slides Completed!</h3>
                    <p className="text-white/50 mb-6">Ready to take the exam?</p>
                    <div className="flex justify-center gap-3">
                      <Button variant="outline" onClick={() => { setCurrentSlide(0); setSlideshowCompleted(false); }} className="border-white/10 text-white/50">Review Slides</Button>
                      <Button onClick={startExam} className="bg-gradient-to-r from-[#00F0FF] to-[#B026FF] text-white" data-testid="take-exam-btn"><Play className="w-4 h-4 mr-2" />Take Exam</Button>
                    </div>
                  </div>
                )}
              </div>
              {!slideshowCompleted && (
                <div className="p-4 border-t border-white/5 flex items-center justify-between bg-black/30">
                  <Button variant="outline" onClick={prevSlide} disabled={currentSlide === 0} className="border-white/10 text-white/50 disabled:opacity-30" data-testid="prev-slide-btn">
                    <ChevronLeft className="w-4 h-4 mr-1" />Previous
                  </Button>
                  <div className="flex gap-1">
                    {selectedModule.slides.map((_, idx) => (
                      <button key={idx} onClick={() => setCurrentSlide(idx)}
                        className={`w-2 h-2 rounded-full transition-all ${idx === currentSlide ? 'bg-[#00F0FF] w-5' : idx < currentSlide ? 'bg-emerald-500' : 'bg-zinc-700'}`} />
                    ))}
                  </div>
                  <Button onClick={nextSlide} className="bg-gradient-to-r from-[#00F0FF] to-[#B026FF] text-white" data-testid="next-slide-btn">
                    {currentSlide === selectedModule.slides.length - 1 ? 'Complete' : 'Next'}<ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Exam Modal */}
      <Dialog open={showExamModal} onOpenChange={setShowExamModal}>
        <DialogContent className="bg-[#0D0D14] border border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><GraduationCap className="w-5 h-5 text-[#00F0FF]" />{selectedModule?.title} - Exam</DialogTitle>
          </DialogHeader>
          {selectedModule && !examResult && (
            <div className="space-y-6 mt-4">
              <div className="p-3 bg-[#00F0FF]/5 border border-[#00F0FF]/15 rounded-lg">
                <p className="text-[#00F0FF] text-xs flex items-center gap-2"><AlertCircle className="w-4 h-4" />Passing score: {selectedModule.passing_score}%</p>
              </div>
              {selectedModule.questions?.map((q, qIndex) => (
                <div key={qIndex} className="p-5 bg-white/[0.02] rounded-xl border border-white/5" data-testid={`question-${qIndex}`}>
                  <p className="text-white font-medium mb-4 flex items-start gap-3">
                    <span className="w-7 h-7 bg-gradient-to-br from-[#00F0FF] to-[#B026FF] rounded-lg flex items-center justify-center text-xs flex-shrink-0">{qIndex + 1}</span>
                    <span className="text-sm">{q.question}</span>
                  </p>
                  <div className="space-y-2 ml-10">
                    {q.options.map((option, oIndex) => (
                      <button key={oIndex} onClick={() => { const a = [...examAnswers]; a[qIndex] = oIndex; setExamAnswers(a); }}
                        data-testid={`answer-${qIndex}-${oIndex}`}
                        className={`w-full text-left p-3 rounded-lg border transition-all text-sm ${
                          examAnswers[qIndex] === oIndex
                            ? 'border-[#00F0FF]/50 bg-[#00F0FF]/10 text-white'
                            : 'border-white/5 hover:border-white/15 text-white/50 hover:text-white'
                        }`}>
                        <span className="mr-2.5 inline-flex items-center justify-center w-5 h-5 rounded text-[10px] bg-white/5">{String.fromCharCode(65 + oIndex)}</span>
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <div className="flex justify-between items-center">
                <span className="text-white/40 text-xs">{examAnswers.filter(a => a !== -1).length} / {selectedModule.questions?.length || 0} answered</span>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setShowExamModal(false)} className="border-white/10 text-white/50">Cancel</Button>
                  <Button onClick={submitExam} disabled={examAnswers.includes(-1)} data-testid="submit-exam-btn"
                    className="bg-gradient-to-r from-[#00F0FF] to-[#B026FF] text-white disabled:opacity-30">Submit Exam</Button>
                </div>
              </div>
            </div>
          )}
          {examResult && (
            <div className="space-y-6 mt-4">
              <div className={`text-center p-8 rounded-xl ${examResult.passed ? 'bg-emerald-500/5 border border-emerald-500/20' : 'bg-red-500/5 border border-red-500/20'}`}>
                {examResult.passed ? (
                  <>
                    <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                      <Trophy className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-emerald-400 mb-1">Congratulations!</h3>
                    <p className="text-white/50 text-sm">You passed and earned your certificate</p>
                  </>
                ) : (
                  <>
                    <XCircle className="w-20 h-20 text-red-400 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-red-400 mb-1">Not Passed</h3>
                    <p className="text-white/50 text-sm">Review the material and try again</p>
                  </>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-4 bg-white/[0.02] rounded-xl border border-white/5"><p className="text-2xl font-bold text-white">{examResult.score}%</p><p className="text-white/30 text-[10px] uppercase tracking-wider">Your Score</p></div>
                <div className="p-4 bg-white/[0.02] rounded-xl border border-white/5"><p className="text-2xl font-bold text-white">{examResult.passing_score}%</p><p className="text-white/30 text-[10px] uppercase tracking-wider">Passing</p></div>
                <div className="p-4 bg-white/[0.02] rounded-xl border border-white/5"><p className="text-2xl font-bold text-white">{examResult.correct_count}/{examResult.total_questions}</p><p className="text-white/30 text-[10px] uppercase tracking-wider">Correct</p></div>
              </div>
              <div className="space-y-2">
                <h4 className="text-white font-medium text-sm">Review Answers</h4>
                {examResult.results?.map((result, idx) => (
                  <div key={idx} className={`p-3 rounded-lg border ${result.is_correct ? 'bg-emerald-500/5 border-emerald-500/15' : 'bg-red-500/5 border-red-500/15'}`}>
                    <div className="flex items-start gap-2">
                      {result.is_correct ? <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />}
                      <div>
                        <p className="text-white text-xs">{result.question}</p>
                        {!result.is_correct && result.explanation && <p className="text-white/50 text-xs mt-1"><span className="text-[#00F0FF]">Tip:</span> {result.explanation}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => { setShowExamModal(false); setExamResult(null); }} className="border-white/10 text-white/50">Close</Button>
                {!examResult.passed && (
                  <Button onClick={() => { setExamResult(null); startSlideshow(); setShowExamModal(false); }} className="bg-gradient-to-r from-[#00F0FF] to-[#B026FF] text-white" data-testid="retry-training-btn">
                    <RotateCcw className="w-4 h-4 mr-2" />Review & Retry
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Training Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="bg-[#0D0D14] border border-white/10 text-white max-w-lg shadow-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-[#00F0FF]" />Assign Training</DialogTitle>
            <DialogDescription className="text-white/50 text-sm">Assign a training module to a staff member</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-xs text-white/50 mb-1.5 uppercase tracking-wider font-medium">Staff Member</label>
              <select value={assignForm.staff_username} onChange={(e) => setAssignForm({...assignForm, staff_username: e.target.value})} data-testid="assign-staff-select"
                className="w-full px-3 py-2.5 bg-[#05050A] border border-white/10 rounded-lg text-white text-sm focus:border-[#00F0FF] focus:outline-none">
                <option value="">Select staff member...</option>
                {staffList.map(s => <option key={s.username} value={s.username}>{s.display_name || s.username}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-1.5 uppercase tracking-wider font-medium">Training Module</label>
              <select value={assignForm.module_id} onChange={(e) => setAssignForm({...assignForm, module_id: e.target.value})} data-testid="assign-module-select"
                className="w-full px-3 py-2.5 bg-[#05050A] border border-white/10 rounded-lg text-white text-sm focus:border-[#00F0FF] focus:outline-none">
                <option value="">Select module...</option>
                {modules.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-white/50 mb-1.5 uppercase tracking-wider font-medium">Due Date</label>
                <input type="date" value={assignForm.due_date} onChange={(e) => setAssignForm({...assignForm, due_date: e.target.value})} data-testid="assign-due-date"
                  className="w-full px-3 py-2.5 bg-[#05050A] border border-white/10 rounded-lg text-white text-sm focus:border-[#00F0FF] focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1.5 uppercase tracking-wider font-medium">Priority</label>
                <select value={assignForm.priority} onChange={(e) => setAssignForm({...assignForm, priority: e.target.value})} data-testid="assign-priority-select"
                  className="w-full px-3 py-2.5 bg-[#05050A] border border-white/10 rounded-lg text-white text-sm focus:border-[#00F0FF] focus:outline-none">
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-1.5 uppercase tracking-wider font-medium">Notes</label>
              <textarea value={assignForm.notes} onChange={(e) => setAssignForm({...assignForm, notes: e.target.value})} rows="2" data-testid="assign-notes"
                className="w-full px-3 py-2.5 bg-[#05050A] border border-white/10 rounded-lg text-white text-sm focus:border-[#00F0FF] focus:outline-none resize-none" placeholder="Optional notes..." />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowAssignModal(false)} className="border-white/10 text-white/50">Cancel</Button>
              <Button onClick={handleAssign} className="bg-gradient-to-r from-[#00F0FF] to-[#B026FF] text-white" data-testid="confirm-assign-btn">Assign Training</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EPortalPageRevamped;
